import { useRef, useState, useCallback, useEffect } from "react";

interface UseAudioCaptureOptions {
  onAudioChunk: (chunk: Blob) => void;
  chunkIntervalMs?: number;
  onSilence?: () => void;
  /** Absolute RMS floor. The VAD also calibrates against the room, and uses whichever is higher. */
  silenceThreshold?: number;
  silenceDurationMs?: number;
}

interface UseAudioCaptureReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  isRecording: boolean;
  error: string | null;
}

/** getUserMedia can hang indefinitely (dismissed permission sheet, wedged audio stack). */
const GET_USER_MEDIA_TIMEOUT_MS = 15000;
const VAD_FRAME_MS = 50;
/** Frames above threshold before we call it speech — rejects door slams and key clicks. */
const SPEECH_CONFIRM_FRAMES = 3;
/**
 * Dead time before the VAD looks at anything at all.
 *
 * A Bluetooth headset (AirPods and friends) has to leave A2DP and negotiate HFP/SCO before its
 * mic produces audio, which takes up to a second and often opens with a burst of switching noise
 * or DC offset. Calibrating the noise floor against that burst would leave the threshold so high
 * that speech never clears it — the mic looks alive and nothing is ever detected.
 */
const VAD_WARMUP_MS = 600;
/** Noise floor must be measured for this long before the VAD is allowed to arm. */
const NOISE_CALIBRATION_MS = 300;
/** Speech has to clear the measured room noise by this factor. */
const NOISE_FLOOR_MULTIPLIER = 3;
/**
 * Hard ceiling on the measured noise floor. Speech sits around 0.05-0.3 RMS, so without this a
 * noisy warm-up or a burst of room noise can push the threshold past anything a voice can reach
 * and silently disable detection for the whole utterance.
 */
const MAX_NOISE_FLOOR = 0.05;

function getSupportedMimeType(): string | null {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return null;
}

function describeMediaError(err: any): string {
  switch (err?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Microphone access denied";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No microphone found";
    case "NotReadableError":
    case "TrackStartError":
      // Another app/tab holds the device, or a previous stream was never released.
      return "Microphone is busy — close other apps using it and try again";
    case "OverconstrainedError":
      return "Microphone does not support the requested settings";
    case "TimeoutError":
      return "Microphone did not respond";
    case "NotSupportedError":
      return "Audio recording is not supported in this browser";
    default:
      return "Failed to start recording";
  }
}

/**
 * getUserMedia with a deadline. A stream that arrives after the deadline is stopped rather than
 * leaked — otherwise the mic stays hot with nothing reading it and the next attempt hits
 * NotReadableError.
 */
async function getMicStream(): Promise<MediaStream> {
  const request = navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });

  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const deadline = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      timedOut = true;
      const err = new Error("getUserMedia timed out");
      err.name = "TimeoutError";
      reject(err);
    }, GET_USER_MEDIA_TIMEOUT_MS);
  });

  request
    .then((stream) => {
      if (timedOut) {
        stream.getTracks().forEach((track) => track.stop());
      }
    })
    .catch(() => {});

  try {
    return await Promise.race([request, deadline]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export function useAudioCapture({
  onAudioChunk,
  chunkIntervalMs = 1000,
  onSilence,
  silenceThreshold = 0.01,
  silenceDurationMs = 2000,
}: UseAudioCaptureOptions): UseAudioCaptureReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onAudioChunkRef = useRef(onAudioChunk);
  const onSilenceRef = useRef(onSilence);
  // One AudioContext for the whole session. Browsers cap concurrent contexts (~6 in Chrome) and
  // close() is async, so creating one per utterance eventually throws and kills the mic for good.
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const vadIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasSpokeRef = useRef(false);
  const startInFlightRef = useRef<Promise<void> | null>(null);
  const unmountedRef = useRef(false);

  onAudioChunkRef.current = onAudioChunk;
  onSilenceRef.current = onSilence;

  const stopVad = useCallback(() => {
    if (vadIntervalRef.current) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        /* already detached */
      }
      sourceRef.current = null;
    }
    analyserRef.current = null;
    silenceStartRef.current = null;
    hasSpokeRef.current = false;
  }, []);

  const startVad = useCallback(
    async (stream: MediaStream) => {
      const AudioCtx: typeof AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;

      if (!audioContextRef.current || audioContextRef.current.state === "closed") {
        audioContextRef.current = new AudioCtx();
      }
      const audioContext = audioContextRef.current;

      // Safari/iOS hand back a suspended context outside a user gesture. A suspended context
      // feeds the analyser nothing but zeroes, so the VAD would see permanent silence, never
      // arm, and the utterance would never end.
      if (audioContext.state === "suspended") {
        try {
          await audioContext.resume();
        } catch {
          /* fall through — checked below */
        }
      }
      if (audioContext.state !== "running") {
        // No VAD available; the user can still stop manually. Worth a log — in hands-free mode
        // this is why the utterance never ends on its own.
        console.warn(
          `[useAudioCapture] AudioContext is ${audioContext.state}; silence detection disabled`
        );
        return;
      }

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      sourceRef.current = source;
      analyserRef.current = analyser;

      const dataArray = new Float32Array(analyser.fftSize);
      const startedAt = Date.now();
      let noiseFloor = silenceThreshold;
      let loudFrames = 0;
      let warmedUp = false;
      let calibrationStartedAt = startedAt;

      vadIntervalRef.current = setInterval(() => {
        const activeAnalyser = analyserRef.current;
        if (!activeAnalyser) return;

        activeAnalyser.getFloatTimeDomainData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / dataArray.length);

        const elapsed = Date.now() - startedAt;

        // Ignore the device settle window outright — see VAD_WARMUP_MS.
        if (elapsed < VAD_WARMUP_MS) return;
        if (!warmedUp) {
          warmedUp = true;
          calibrationStartedAt = Date.now();
        }

        const calibrating = Date.now() - calibrationStartedAt < NOISE_CALIBRATION_MS;
        // A fixed threshold fails both ways: in a noisy room it never drops below, so the
        // utterance never ends; on a quiet/low-gain mic it never rises above, so speech is never
        // detected. Track the room instead.
        const threshold = Math.max(
          silenceThreshold,
          noiseFloor * NOISE_FLOOR_MULTIPLIER
        );

        if (calibrating) {
          noiseFloor = Math.min(noiseFloor * 0.7 + rms * 0.3, MAX_NOISE_FLOOR);
          return;
        }

        if (rms > threshold) {
          loudFrames++;
          if (loudFrames >= SPEECH_CONFIRM_FRAMES) {
            hasSpokeRef.current = true;
            silenceStartRef.current = null;
          }
          return;
        }

        loudFrames = 0;
        // Keep adapting to the room while it's quiet, so a fan spinning up mid-utterance
        // doesn't permanently mask the silence detector.
        noiseFloor = Math.min(noiseFloor * 0.98 + rms * 0.02, MAX_NOISE_FLOOR);

        if (!hasSpokeRef.current) return;

        if (silenceStartRef.current === null) {
          silenceStartRef.current = Date.now();
        } else if (Date.now() - silenceStartRef.current > silenceDurationMs) {
          silenceStartRef.current = null;
          hasSpokeRef.current = false;
          onSilenceRef.current?.();
        }
      }, VAD_FRAME_MS);
    },
    [silenceThreshold, silenceDurationMs]
  );

  /** Release everything without touching React state — safe to call from any path. */
  const teardownCapture = useCallback(() => {
    stopVad();
    const mediaRecorder = mediaRecorderRef.current;
    if (mediaRecorder) {
      mediaRecorder.ondataavailable = null;
      mediaRecorder.onerror = null;
      if (mediaRecorder.state !== "inactive") {
        try {
          mediaRecorder.stop();
        } catch {
          /* already stopped */
        }
      }
      mediaRecorderRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.onended = null;
        track.stop();
      });
      streamRef.current = null;
    }
  }, [stopVad]);

  const startRecording = useCallback(async () => {
    // Several Dictaphone effects can race to start in the same commit. Without this, each call
    // opens its own stream/recorder and overwrites the refs, orphaning the previous ones — the
    // mic stays captured and the device eventually refuses new streams.
    if (startInFlightRef.current) return startInFlightRef.current;
    if (mediaRecorderRef.current || streamRef.current) {
      teardownCapture();
    }

    const run = async () => {
      try {
        setError(null);

        if (typeof MediaRecorder === "undefined") {
          throw Object.assign(new Error("MediaRecorder unavailable"), {
            name: "NotSupportedError",
          });
        }
        if (!navigator.mediaDevices?.getUserMedia) {
          // Almost always an insecure origin (http://<lan-ip>): getUserMedia is only exposed in
          // a secure context.
          throw Object.assign(new Error("getUserMedia unavailable"), {
            name: window.isSecureContext ? "NotFoundError" : "SecurityError",
          });
        }

        const stream = await getMicStream();

        // The hook was torn down (or another start won) while we waited on the permission sheet.
        if (unmountedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        // Which device the browser actually handed us. `sampleRate` is the tell for Bluetooth: a
        // headset mic runs over HFP/SCO at 8k or 16k, so 48000 with "AirPods" in the label means
        // the output is on the headset but the INPUT is still the built-in mic.
        const audioTrack = stream.getAudioTracks()[0];
        const settings: any = audioTrack?.getSettings?.() ?? {};
        console.log(
          `[useAudioCapture] mic "${audioTrack?.label || "(no label)"}" ` +
            `sampleRate=${settings.sampleRate ?? "?"} channels=${settings.channelCount ?? "?"} ` +
            `deviceId=${settings.deviceId ?? "?"}`
        );

        const mimeType = getSupportedMimeType();
        const mediaRecorder = new MediaRecorder(
          stream,
          mimeType ? { mimeType, audioBitsPerSecond: 32000 } : undefined
        );
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            onAudioChunkRef.current(event.data);
          }
        };

        mediaRecorder.onerror = () => {
          teardownCapture();
          setError("MediaRecorder error occurred");
          setIsRecording(false);
        };

        // Fires when the OS takes the device away (unplugged headset, another app grabs it).
        // Programmatic track.stop() does NOT fire this, so it only ever means a real loss.
        stream.getTracks().forEach((track) => {
          track.onended = () => {
            if (streamRef.current !== stream) return;
            teardownCapture();
            setError("Microphone disconnected");
            setIsRecording(false);
          };
        });

        mediaRecorder.start(chunkIntervalMs);
        setIsRecording(true);

        if (onSilenceRef.current) {
          await startVad(stream);
        }
      } catch (err: any) {
        // Anything after getUserMedia can throw (unsupported mimeType, recorder in a bad state).
        // Release the stream we already hold, or the mic stays live behind a failed start.
        teardownCapture();
        setError(describeMediaError(err));
        setIsRecording(false);
      }
    };

    const pending = run().finally(() => {
      startInFlightRef.current = null;
    });
    startInFlightRef.current = pending;
    return pending;
  }, [chunkIntervalMs, startVad, teardownCapture]);

  const stopRecording = useCallback(async () => {
    // A stop issued while start is still awaiting the permission sheet must not run first, or
    // the recorder it creates outlives the stop.
    if (startInFlightRef.current) {
      await startInFlightRef.current.catch(() => undefined);
    }

    stopVad();

    await new Promise<void>((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      const finalizeStop = () => {
        teardownCapture();
        setIsRecording(false);
        resolve();
      };

      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.addEventListener("stop", finalizeStop, { once: true });
        // If the recorder never emits 'stop' (backgrounded tab on mobile), finalize anyway
        // rather than leaving the caller awaiting forever.
        setTimeout(() => {
          if (mediaRecorderRef.current === mediaRecorder) finalizeStop();
        }, 1000);
        try {
          mediaRecorder.stop();
        } catch {
          finalizeStop();
        }
        return;
      }

      finalizeStop();
    });
  }, [stopVad, teardownCapture]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      teardownCapture();
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close().catch(() => {});
      }
      audioContextRef.current = null;
    };
  }, [teardownCapture]);

  return { startRecording, stopRecording, isRecording, error };
}
